import { PRESENCE_AUTO_AWAY_S, PRESENCE_HEARTBEAT_S, PRESENCE_STATUS_VALUES, type PresenceStatus } from "@sagot/constants";
import type { IconName } from "@sagot/ui";

/** How a status looks: icon + text + colour (never colour alone) and the i18n keys of its label and hint. */
export interface PresenceMeta {
  status: PresenceStatus;
  icon: IconName;
  /** Semantic-token class of the dot. */
  dot: string;
  label: string;
  hint: string;
}

const PRESENCE_ROOT = "routingLive.presence";

export const PRESENCE_META: Readonly<Record<PresenceStatus, PresenceMeta>> = {
  online: { status: "online", icon: "check-circle", dot: "bg-success", label: `${PRESENCE_ROOT}.online`, hint: `${PRESENCE_ROOT}.hintOnline` },
  away: { status: "away", icon: "pause-circle", dot: "bg-fg-subtle", label: `${PRESENCE_ROOT}.away`, hint: `${PRESENCE_ROOT}.hintAway` },
  busy: { status: "busy", icon: "alert-circle", dot: "bg-warning", label: `${PRESENCE_ROOT}.busy`, hint: `${PRESENCE_ROOT}.hintBusy` },
  offline: { status: "offline", icon: "circle", dot: "bg-line-strong", label: `${PRESENCE_ROOT}.offline`, hint: `${PRESENCE_ROOT}.hintOffline` },
};

/** The statuses a member may pick, in menu order. */
export const PRESENCE_CHOICES: readonly PresenceStatus[] = PRESENCE_STATUS_VALUES;

export const HEARTBEAT_MS = PRESENCE_HEARTBEAT_S * 1000;
export const AUTO_AWAY_MS = PRESENCE_AUTO_AWAY_S * 1000;
/** How often the console looks at whether a heartbeat is due. */
export const BEAT_CHECK_MS = 5_000;

/** The label of a status; an automatic away says why. */
export function statusLabelKey(status: PresenceStatus, autoAway: boolean): string {
  return autoAway ? `${PRESENCE_ROOT}.autoAway` : PRESENCE_META[status].label;
}

export interface BeatInput {
  /** What the server reports for the member now (`status` is what it reads as). */
  status: PresenceStatus;
  autoAway: boolean;
  /** Last pointer / key / focus in the console, and the last heartbeat sent (0 = none yet). */
  lastActivityAt: number;
  lastBeatAt: number;
  now: number;
}

/**
 * Whether to send the member's `online` heartbeat now. Only a member who is online (or was put away by silence) beats, and
 * only when they did something since the last beat, so silence reaches the server within a minute of the last real activity
 * and auto-away follows five minutes later. Coming back from an automatic away beats at once.
 */
export function beatDue(input: BeatInput): boolean {
  const { status, autoAway, lastActivityAt, lastBeatAt, now } = input;
  if (status !== "online" && !autoAway) return false;
  if (lastActivityAt <= lastBeatAt) return false;
  return autoAway || now - lastBeatAt >= HEARTBEAT_MS;
}
